import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Card, Form, Input, Button, Typography, Row, Col, Modal } from "antd";
import {
  MailOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  BankOutlined,
  TeamOutlined,
  ReadOutlined,
  LineChartOutlined,
  RocketOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import CompanyRegister from "../components/auth/CompanyRegister";
import AssociationRegister from "../components/auth/AssociationRegister";
import AcademiaRegister from "../components/auth/AcademiaRegister";
import IncubatorRegister from "../components/auth/IncubatorRegister";
import StartupRegister from "../components/auth/StartupRegister";
import InvestorRegister from "../components/auth/InvestorRegister";
import ConsultantRegister from "../components/auth/ConsultantRegister";
import {
  FaBuilding,
  FaUsers,
  FaRocket,
  FaSeedling,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaGoogle,
  FaApple,
  FaGraduationCap,
  FaChartLine,
  FaUniversity,
} from "react-icons/fa";

const { Text, Title } = Typography;

const LoginScreen = () => {
  const [loading, setLoading] = useState(false);
  const [showRegisterOptions, setShowRegisterOptions] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const onFinish = async (values) => {
    setLoading(true);

    const loginData = {
      email: values.email.trim(),
      password: values.password,
    };
    // console.log('Login data to be sent:', loginData);
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        loginData,
        config
      );
      // console.log("Login response data:", res.data);
      // return;
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);

        if (res.data.user) {
          localStorage.setItem("userInfo", JSON.stringify(res.data.user));
        } else {
          const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
            headers: { "x-auth-token": res.data.token },
          });
          if (userRes.data) {
            localStorage.setItem("userInfo", JSON.stringify(userRes.data));
            res.data.user = userRes.data;
          }
        }

        const userRole =
          res.data.user?.role ||
          JSON.parse(localStorage.getItem("userInfo"))?.role;

        // Note: admin approval status only controls public visibility on the
        // website (see AID-* public listing routes), not login access. Users
        // can always log in and edit their profile while pending/rejected.

        toast.success("Login successful!");

        // Route based on user role
        if (userRole === "superadmin" || userRole === "admin") {
          navigate("/admin");
        } else if (userRole === "company") {
          navigate("/dashboard/company_profile");
        } else if (userRole === "association") {
          navigate("/dashboard/association_profile");
        } else if (userRole === "startup") {
          navigate("/dashboard/startup_profile");
        } else if (userRole === "incubator") {
          navigate("/dashboard/incubator_profile");
        } else if (userRole === "investor") {
          navigate("/dashboard/investor_profile");
        } else if (userRole === "academia") {
          navigate("/dashboard/academia_profile");
        } else if (userRole === "consultant") {
          navigate("/dashboard/consultant_profile");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        errors.forEach((error) => toast.error(error.msg));
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = (type) => {
    setModalType(type);
    setModalOpen(true);
    setShowRegisterOptions(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalType("");
  };

  const renderModalContent = () => {
    switch (modalType) {
      case "company":
        return <CompanyRegister onSuccess={closeModal} isModal={true} />;
      case "association":
        return <AssociationRegister onSuccess={closeModal} isModal={true} />;
      case "academia":
        return <AcademiaRegister onSuccess={closeModal} isModal={true} />;
      case "incubator":
        return <IncubatorRegister onSuccess={closeModal} isModal={true} />;
      case "startup":
        return <StartupRegister onSuccess={closeModal} isModal={true} />;
      case "investor":
        return <InvestorRegister onSuccess={closeModal} isModal={true} />;
      case "consultant":
        return <ConsultantRegister onSuccess={closeModal} isModal={true} />;
      default:
        return null;
    }
  };

  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontWeight: 400,
    color: "#333",
  };

  const accountTypes = [
    {
      key: "company",
      title: "Company",
      description: "Register as a business or industrial company",
      icon: <BankOutlined style={{ fontSize: "32px", color: "#fff" }} />,
      color: "#470172",
    },
    {
      key: "association",
      title: "Association",
      description: "Register as an industry association or group",
      icon: <TeamOutlined style={{ fontSize: "32px", color: "#fff" }} />,
      color: "#470172",
    },
    {
      key: "academia",
      title: "Academia",
      description: "Register as an academic institution",
      icon: <ReadOutlined style={{ fontSize: "32px", color: "#fff" }} />,
      color: "#470172",
    },
    {
      key: "investor",
      title: "Investor",
      description: "Register as an investor or funding organization",
      icon: <LineChartOutlined style={{ fontSize: "32px", color: "#fff" }} />,
      color: "#470172",
    },
    {
      key: "startup",
      title: "Startup",
      description: "Register as a startup or new venture",
      icon: <RocketOutlined style={{ fontSize: "32px", color: "#fff" }} />,
      color: "#470172",
    },
    {
      key: "incubator",
      title: "Incubator",
      description: "Register as an incubator or accelerator",
      icon: <BulbOutlined style={{ fontSize: "32px", color: "#fff" }} />,
      color: "#470172",
    },
    {
      key: "consultant",
      title: "Consultant",
      description: "Register as a consultant or advisory professional",
      icon: <TeamOutlined style={{ fontSize: "32px", color: "#fff" }} />,
      color: "#470172",
    },
  ];

  return (
    <div className="flex w-screen h-screen" style={{ position: "relative" }}>
      <style>{`
        @media (max-width: 768px) {
          .login-bg-image {
            display: none !important;
          }
          .login-card-wrap {
            justify-content: center !important;
            padding: 20px !important;
          }
          .login-card-body .ant-card-body {
            padding: 32px 20px !important;
          }
        }
      `}</style>
      {/* Background Image - Full Screen (hidden on mobile) */}
      <div
        className="login-bg-image absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('./img/back.jpeg')",
        }}
      ></div>

      {/* Login Card - Right Side */}
      <div className="login-card-wrap relative w-full h-full flex items-center justify-end p-8 pr-24">
        <Card
          className="login-card-body"
          style={{
            maxWidth: "500px",
            width: "100%",
            borderRadius: "12px",
            boxShadow:
              "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e0e0e0",
            backgroundColor: "#ffffff",
            transition: "all 0.3s ease",
            overflowY: "auto",
            maxHeight: "100vh"
          }}
          bodyStyle={{
            padding: "48px 40px",
          }}
        >
          {!showRegisterOptions ? (
            <>
              {/* Logo */}
              <div style={{ textAlign: "center", marginBottom: "32px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <img
                  src="./img/aid.svg"
                  alt="AID Logo"
                  style={{
                    width: "120px",
                    height: "auto",
                    marginBottom: "24px",
                    display: "block",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <Title
                  level={3}
                  style={{
                    ...baseStyle,
                    fontSize: "26px",
                    fontWeight: 500,
                    margin: "0 0 8px 0",
                    lineHeight: "1.3",
                    textAlign: "center",
                  }}
                >
                  Welcome to the
                </Title>
                <Title
                  level={4}
                  style={{
                    // ...baseStyle,
                    fontSize: "22px",
                    fontWeight: 500,
                    margin: "0 0 4px 0",
                    lineHeight: "1.4",
                    textAlign: "center",
                    color: "#470172",
                  }}
                >
                  Association For Industrial Development
                </Title>
                <Text
                  style={{
                    ...baseStyle,
                    fontSize: "16px",
                    opacity: 0.7,
                    display: "block",
                    marginTop: "12px",
                    textAlign: "center",
                  }}
                >
                  Login to your account
                </Text>
              </div>

              {/* Login Form */}
              <Form
                form={form}
                name="login"
                onFinish={onFinish}
                layout="vertical"
                requiredMark={false}
                style={{ marginTop: "32px" }}
              >
                <style>
                  {`
                    .ant-input,
                    .ant-input-password input {
                      text-align: center !important;
                      transition: text-align 0.3s ease !important;
                    }
                    .ant-input::placeholder,
                    .ant-input-password input::placeholder {
                      text-align: center !important;
                      font-family: 'Rubik', sans-serif !important;
                      font-size: 15px !important;
                      font-weight: 400 !important;
                      color: #999 !important;
                      line-height: 48px !important;
                      display: flex !important;
                      align-items: center !important;
                      justify-content: center !important;
                    }
                    .ant-input-affix-wrapper {
                      display: flex !important;
                      align-items: center !important;
                    }
                    .ant-input-prefix {
                      display: flex !important;
                      align-items: center !important;
                      justify-content: center !important;
                    }
                    .ant-input:focus,
                    .ant-input-focused,
                    .ant-input-password input:focus,
                    .ant-input-affix-wrapper-focused,
                    .ant-input-affix-wrapper:focus-within {
                      background-color: transparent !important;
                      box-shadow: 0 0 0 2px rgba(71, 1, 114, 0.1) !important;
                    }
                    .ant-input:focus,
                    .ant-input-password input:focus {
                      text-align: left !important;
                    }
                    .ant-input:focus::placeholder,
                    .ant-input-password input:focus::placeholder {
                      text-align: left !important;
                      transition: text-align 0.3s ease !important;
                    }
                    .ant-input:not(:placeholder-shown),
                    .ant-input-password input:not(:placeholder-shown) {
                      text-align: left !important;
                    }
                  `}
                </style>

                {/* Email Field */}
                <Form.Item
                  name="email"
                  label={
                    <span style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: "17px",
                      fontWeight: 400,
                      color: "#470172",
                      display: "block",
                      marginBottom: "8px"
                    }}>
                      Email Address
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                  style={{ marginBottom: "20px" }}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: "#470172", fontSize: "16px", marginRight: "8px" }} />}
                    placeholder="you@example.com"
                    size="large"
                    style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: "15px",
                      fontWeight: 400,
                      height: "48px",
                      padding: "0 16px",
                      borderRadius: "8px",
                      border: "1.5px solid #e0e0e0",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center"
                    }}
                  />
                </Form.Item>

                {/* Password Field */}
                <Form.Item
                  name="password"
                  label={
                    <span style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: "17px",
                      fontWeight: 400,
                      color: "#470172",
                      display: "block",
                      marginBottom: "8px"
                    }}>
                      Password
                    </span>
                  }
                  rules={[{ required: true, message: "Please enter your password" }]}
                  style={{ marginBottom: "24px" }}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#470172", fontSize: "16px", marginRight: "8px" }} />}
                    placeholder="Enter your password"
                    size="large"
                    iconRender={(visible) =>
                      visible ?
                        <EyeTwoTone twoToneColor="#470172" style={{ fontSize: "18px" }} /> :
                        <EyeInvisibleOutlined style={{ color: "#999", fontSize: "18px" }} />
                    }
                    style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: "15px",
                      fontWeight: 400,
                      height: "48px",
                      padding: "0 16px",
                      borderRadius: "8px",
                      border: "1.5px solid #e0e0e0",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center"
                    }}
                  />
                </Form.Item>

                {/* Login Button */}
                <Form.Item style={{ marginBottom: "20px", display: 'flex', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    style={{
                      fontFamily: "'Rubik', sans-serif",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: 400,
                      height: "40px",
                      width: "100%",
                      maxWidth: "280px",
                      backgroundColor: "#470172",
                      borderColor: "#470172",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(71, 1, 114, 0.25)",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#5a0196";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 6px 16px rgba(71, 1, 114, 0.35)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#470172";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(71, 1, 114, 0.25)";
                    }}
                  >
                    {loading ? "Signing in..." : "Log In"}
                  </Button>
                </Form.Item>
              </Form>

              {/* Sign Up Link */}
              <div style={{ textAlign: "center", marginTop: "18px", paddingTop: "18px", borderTop: "1px solid #f0f0f0" }}>
                <Text style={{ ...baseStyle, fontSize: "15px" }}>
                  Don't have an account?{" "}
                  <Text
                    onClick={() => setShowRegisterOptions(true)}
                    // type="button"
                    style={{
                      ...baseStyle,
                      fontSize: "18px",
                      fontWeight: 500,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textDecoration: "none",
                      padding: 0,
                      color: "#470172",
                    }}
                    onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                    onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                  >
                    Register Now
                  </Text>
                </Text>
              </div>
            </>
          ) : (
            <>
              {/* Registration Options */}
              <div style={{ marginBottom: "24px" }}>
                <Title level={3} style={{ ...baseStyle, fontSize: "24px", fontWeight: 500, textAlign: "center", color: "#470172", marginBottom: "8px" }}>
                  Choose Account Type
                </Title>
                <Text style={{ ...baseStyle, fontSize: "14px", opacity: 0.7, display: "block", textAlign: "center" }}>
                  Select the type of account you want to create
                </Text>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {accountTypes.map((type) => (
                  <Card
                    key={type.key}
                    hoverable
                    onClick={() => handleRegisterClick(type.key)}
                    style={{
                      borderRadius: "8px",
                      border: "2px solid #f0f0f0",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    bodyStyle={{
                      padding: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#470172";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(71, 1, 114, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#f0f0f0";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "10px",
                        backgroundColor: type.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "transform 0.3s ease"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                      onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                    >
                      {type.icon}
                    </div>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <Title
                        level={5}
                        style={{
                          ...baseStyle,
                          fontSize: "16px",
                          fontWeight: 500,
                          margin: 0,
                          marginBottom: "4px",
                          color: "#333"
                        }}
                      >
                        {type.title}
                      </Title>
                      <Text
                        style={{
                          ...baseStyle,
                          fontSize: "13px",
                          color: "#666",
                          display: "block",
                          lineHeight: "1.4"
                        }}
                      >
                        {type.description}
                      </Text>
                    </div>
                  </Card>
                ))}
              </div>


              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <Button
                  size="large"
                  onClick={() => setShowRegisterOptions(false)}
                  style={{
                    width: '40%',
                    maxWidth: '240px',
                    ...baseStyle,
                    fontSize: '15px',
                    fontWeight: 500,
                    height: '48px',
                    borderColor: '#470172',
                    color: '#470172',
                    borderRadius: '6px',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#470172';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#470172';
                  }}
                >
                  Back to Login
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Registration Modal */}
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width="90%"
        style={{ maxWidth: "900px", maxHeight: "96vh" }}
        bodyStyle={{ maxHeight: "calc(90vh - 55px)", overflowY: "auto", padding: "24px" }}
        centered
        destroyOnClose={false}
        maskClosable={false}
        title={
          <span style={{ fontSize: "20px", fontWeight: 500, color: "#470172", fontFamily: "Rubik, sans-serif" }}>
            {modalType
              ? `${modalType.charAt(0).toUpperCase() + modalType.slice(1)} Registration`
              : "Registration"}
          </span>
        }
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default LoginScreen;